import Card from "components/card";
import React, { useState, useEffect, useCallback } from "react";
import SubscriptionsDataTable from "./components/SubscriptionsDataTable";
import Loader from "components/Loader";
import { subscriptionsApi } from "api/models";
import { Subscription, SubscriptionsListManagementParams } from "types";
import { handleAxiosAndCaughtError } from "utils";
import { useSearchParams, useParams } from "react-router-dom";

const Subscriptions = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  
  // Get the subscription ID from URL params (e.g., /subscriptions/123) or query params (e.g., /subscriptions?id=123)
  const subscriptionIdFromUrl = id || searchParams.get('id');

  // State management
  const [data, setData] = useState<Subscription[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Fetch data function - fetch all data once
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const requestParams: SubscriptionsListManagementParams = {
        skip: 0,
        take: 999, // Fetch all data
      };
      
      const response = await subscriptionsApi.getListManagement(requestParams);
      setData(response.items || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      handleAxiosAndCaughtError(err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch data on mount only
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleRefetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // List management object for compatibility - no search/filter handlers needed
  const listManagement = {
    data,
    total,
    isLoading,
    error,
    refetch: handleRefetch,
  };

  return (
    <Card extra={" relative w-full h-[86vh] flex flex-col md:mt-3 mt-12 px-6 pb-2"}>
      {listManagement.isLoading ? (
        <Loader
          txt="Loading subscriptions..."
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
      ) : listManagement.error ? (
        <div
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2
        -translate-y-1/2 flex-col items-center justify-center py-10
         "
        >
          <span className="mb-4 text-red-500">{String(listManagement.error)}</span>
          <button
            className="rounded bg-brand-500 px-4 py-2 text-white transition hover:bg-brand-600"
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      ) : (
        <SubscriptionsDataTable 
          listManagement={listManagement}
          initialSubscriptionId={subscriptionIdFromUrl}
        />
      )}
    </Card>
  );
};

export default Subscriptions;
